import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/site/PageHeader";
import { CTASection } from "@/components/site/CTASection";
import about1 from "@/assets/mp/about1.jpg.asset.json";
import michaelOwner from "@/assets/michael-owner.png.asset.json";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — Michael's Painting" },
      { name: "description", content: "Locally focused Wisconsin painting company built on quality workmanship, clear communication, and respect for your property." },
      { property: "og:title", content: "About Michael's Painting" },
      { property: "og:description", content: "Locally focused Wisconsin painting company built on quality workmanship and clear communication." },
      { property: "og:url", content: "https://michaelspaintingwi.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/about" }],
  }),
  component: AboutPage,
});

const PILLARS = [
  { t: "30+ years in Madison", d: "Three decades of painting homes and businesses across south-central Wisconsin." },
  { t: "1,200+ projects completed", d: "From single rooms to multi-building commercial repaints — references on request." },
  { t: "Quality workmanship", d: "Trained crews, premium product, and zero shortcuts in preparation." },
  { t: "Clear communication", d: "One project lead, honest timelines, and updates you don't have to chase." },
  { t: "Respect for property", d: "Floors covered, furniture protected, surfaces left cleaner than we found them." },
  { t: "3-year written warranty", d: "Every interior and exterior project is backed in writing for your peace of mind." },
];

function AboutPage() {
  return (
    <>
      <PageHeader eyebrow="About" title="Madison's trusted painting team for over 30 years." subtitle="A family-owned painting company serving homeowners, businesses, and builders across south-central Wisconsin." />
      <section className="py-24 lg:py-32 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <img src={about1.url} alt="Michael's Painting craftsman at work" loading="lazy" className="rounded-md w-full" />
          <div>
            <h2 className="font-serif text-4xl text-brand-navy mb-6 text-balance">Painters who actually care how it ends.</h2>
            <p className="text-zinc-400 leading-relaxed mb-4">Michael's Painting has been transforming homes and businesses in the Madison area for more than 30 years. What started as a one-person operation has grown into one of south-central Wisconsin's most trusted painting and finishing companies.</p>
            <p className="text-zinc-400 leading-relaxed">We handle interior and exterior painting, cabinet finishing, drywall and framing, pressure washing, graffiti removal, and epoxy flooring — with the same attention to detail on every project, whether it's a single room or a multi-building commercial repaint.</p>
          </div>
        </div>
      </section>
      <section className="py-24 bg-brand-ivory-soft">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div className="order-2 lg:order-1">
              <span className="text-brand-brass font-sans text-sm tracking-widest uppercase">Meet the Owner</span>
              <h2 className="font-serif text-4xl text-brand-navy mt-2 mb-6 text-balance">Michael</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                With over 30 years of hands-on experience, Michael has built Michael's Painting into one of south-central Wisconsin's most trusted painting and finishing companies. His commitment to quality workmanship, honest communication, and treating every property with respect is the foundation of everything we do.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                From the first call to the final brushstroke, Michael personally ensures every project meets the high standards that have defined his reputation for three decades.
              </p>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <img
                  src={michaelOwner.url}
                  alt="Michael, owner of Michael's Painting"
                  loading="lazy"
                  className="rounded-md w-full max-w-md object-contain"
                />
              </div>
            </div>
          </div>
          <h2 className="font-serif text-4xl text-brand-navy mb-12 text-center">What we stand for</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.t} className="p-8 bg-brand-ivory rounded-md border border-zinc-200/60">
                <span className="font-serif italic text-brand-brass text-2xl">0{i + 1}</span>
                <h3 className="font-serif text-xl text-brand-navy mt-3 mb-2">{p.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
